import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Headset, X, Clock, UserCog, WifiOff, Lock, ArrowLeft } from "lucide-react";
import { UsuarioContext } from "../../../providers/userContext";
import useChatEliteSocket from "../../../hooks/useChatEliteSocket";
import { getResumenChatElite, getMisTicketsChatElite, getTicketChatElite } from "../../../services/chatElite.service";
import { avisarUnaVez } from "../../../utilities/chatEliteAvisos";
import { textoHorario } from "../../../utilities/chatEliteFormato";
import { getSuplantadorChatElite, puedeUsarWidgetChatElite } from "../../../utilities/chatEliteUsuario";
import ListaConsultas from "./ListaConsultas";
import NuevaConsulta from "./NuevaConsulta";
import ConversacionAaff from "./ConversacionAaff";
import { ponerBorrador } from "../comun/Compositor";
import { EVENTO_ABRIR_CHAT_ELITE, EVENTO_ABRIR_TICKET_CHAT_ELITE } from "../../../utilities/chatEliteAbrir";
import { useContadorFavicon } from "../../../utilities/faviconContador";
import "../comun/ChatEliteComun.css";
import "./ChatEliteWidget.css";

// En estas paginas hay un chat propio abajo a la derecha: en movil el boton se mueve a la izquierda
const RUTAS_DESPLAZADO = ["/ia-juridica", "/ia-normativa"];
const REFRESCO_DEBOUNCE_MS = 250;

const Widget = ({ user }) => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const suplantador = useMemo(() => getSuplantadorChatElite(user), [user]);

  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState("lista"); // lista | nueva | ticket
  const [bandeja, setBandeja] = useState("activos");
  const [tickets, setTickets] = useState({ activos: [], resueltos: [] });
  const [cargandoLista, setCargandoLista] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [noLeidosAlAbrir, setNoLeidosAlAbrir] = useState(0);
  const [resumen, setResumen] = useState({ activos: 0, noLeidos: 0, horario: null });
  const [noDisponible, setNoDisponible] = useState(false);
  // Cambia para volver a montar "Nueva consulta" con un borrador nuevo (abierto desde Tu IA Normativa)
  const [claveNueva, setClaveNueva] = useState(0);

  const viendoTicketId = abierto && vista === "ticket" ? ticket?.id ?? null : null;
  const { socket, conectado } = useChatEliteSocket({
    activo: !noDisponible,
    enviarEstado: !suplantador, // solo el AAFF real informa de donde esta
    chatAbierto: abierto,
    ticketAbierto: viendoTicketId,
  });

  const noLeidosRef = useRef(0);
  const viendoRef = useRef(null);
  viendoRef.current = viendoTicketId;
  const estadoRef = useRef({ abierto, vista, bandeja });
  estadoRef.current = { abierto, vista, bandeja };
  const timerRefresco = useRef(null);

  const manejarError = useCallback((error) => {
    if (error?.code === "CHAT_ELITE_NO_DISPONIBLE") setNoDisponible(true);
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const r = await getResumenChatElite();
      setResumen(r);
      const antes = noLeidosRef.current;
      noLeidosRef.current = r.noLeidos || 0;
      return { antes, ahora: noLeidosRef.current };
    } catch (error) {
      manejarError(error);
      return null;
    }
  }, [manejarError]);

  const cargarLista = useCallback(async (b = estadoRef.current.bandeja) => {
    setCargandoLista(true);
    try {
      const r = await getMisTicketsChatElite(b);
      setTickets((prev) => ({ ...prev, [b]: r.tickets || [] }));
      if (r.horario) setResumen((prev) => ({ ...prev, horario: r.horario }));
    } catch (error) {
      manejarError(error);
    } finally {
      setCargandoLista(false);
    }
  }, [manejarError]);

  const recargarTicket = useCallback(async (id) => {
    try {
      const r = await getTicketChatElite(id);
      setTicket((actual) => (actual?.id === id ? r.ticket : actual));
    } catch (_) {}
  }, []);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  useEffect(() => {
    if (abierto && vista === "lista") cargarLista(bandeja);
  }, [abierto, vista, bandeja, cargarLista]);

  // Tiempo real: lista, contador, ticket abierto y sonido (una vez aunque haya varias pestañas)
  useEffect(() => {
    if (!socket) return undefined;

    const refrescoDiferido = () => {
      clearTimeout(timerRefresco.current);
      timerRefresco.current = setTimeout(() => {
        const { abierto: ab, vista: v, bandeja: b } = estadoRef.current;
        if (ab && v === "lista") cargarLista(b);
      }, REFRESCO_DEBOUNCE_MS);
    };

    const onMensaje = async (d) => {
      const cambio = await cargarResumen();
      const loEstoyViendo = viendoRef.current === d.ticketId && document.visibilityState === "visible";
      if (cambio && cambio.ahora > cambio.antes && !loEstoyViendo) avisarUnaVez(`m-${d.mensajeId}`);
      refrescoDiferido();
    };
    const onTicket = (d) => {
      cargarResumen();
      refrescoDiferido();
      if (viendoRef.current === d.ticketId) recargarTicket(d.ticketId);
    };
    const onLeido = (d) => {
      if (d.esAaff) {
        cargarResumen(); // lo he leido en otra pestaña
        refrescoDiferido();
      }
    };
    const onConnect = () => {
      cargarResumen();
      refrescoDiferido();
    };
    // Le han quitado el plan Elite: se muestra "no disponible" al momento (el servidor cierra la conexion)
    const onNoDisponible = () => setNoDisponible(true);
    const onDisconnect = (motivo) => {
      if (motivo === "io server disconnect") cargarResumen();
    };

    socket.on("chatElite:noDisponible", onNoDisponible);
    socket.on("disconnect", onDisconnect);
    socket.on("chatElite:mensaje", onMensaje);
    socket.on("chatElite:ticket", onTicket);
    socket.on("chatElite:leido", onLeido);
    socket.on("connect", onConnect);
    return () => {
      socket.off("chatElite:noDisponible", onNoDisponible);
      socket.off("disconnect", onDisconnect);
      socket.off("chatElite:mensaje", onMensaje);
      socket.off("chatElite:ticket", onTicket);
      socket.off("chatElite:leido", onLeido);
      socket.off("connect", onConnect);
      clearTimeout(timerRefresco.current);
    };
  }, [socket, cargarResumen, cargarLista, recargarTicket]);

  // Abrir en "Nueva consulta" con un texto ya escrito (lo lanza Tu IA Normativa al derivar una pregunta de soporte)
  useEffect(() => {
    const onAbrir = (e) => {
      ponerBorrador("nueva-consulta", e.detail?.texto);
      setTicket(null);
      setVista("nueva");
      setClaveNueva((k) => k + 1);
      setAbierto(true);
    };
    window.addEventListener(EVENTO_ABRIR_CHAT_ELITE, onAbrir);
    return () => window.removeEventListener(EVENTO_ABRIR_CHAT_ELITE, onAbrir);
  }, []);

  // Abrir una consulta concreta (la acaba de crear Tu IA Normativa o es una ya enviada desde allí)
  useEffect(() => {
    const onAbrirTicket = async (e) => {
      let tk = e.detail?.ticket;
      if (!tk?.id) return;
      // Solo llega el id: cargar la consulta completa
      if (Object.keys(tk).length <= 1) {
        try {
          tk = (await getTicketChatElite(tk.id)).ticket;
        } catch (error) {
          manejarError(error);
          return;
        }
      }
      setNoLeidosAlAbrir(tk.noLeidos || 0);
      setTicket(tk);
      setBandeja("activos");
      setVista("ticket");
      setAbierto(true);
      cargarResumen();
    };
    window.addEventListener(EVENTO_ABRIR_TICKET_CHAT_ELITE, onAbrirTicket);
    return () => window.removeEventListener(EVENTO_ABRIR_TICKET_CHAT_ELITE, onAbrirTicket);
  }, [manejarError, cargarResumen]);

  // Esc cierra; en movil se bloquea el scroll de la pagina con el chat abierto
  useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !document.querySelector(".ce-visor")) setAbierto(false);
    };
    document.addEventListener("keydown", onKey);
    const movil = window.matchMedia("(max-width: 600px)").matches;
    const overflowPrevio = document.body.style.overflow;
    if (movil) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [abierto]);

  const abrirTicket = (tk) => {
    setNoLeidosAlAbrir(tk.noLeidos || 0);
    setTicket(tk);
    setVista("ticket");
    recargarTicket(tk.id);
  };

  const alCrear = (tk) => {
    setNoLeidosAlAbrir(0);
    setTicket(tk);
    setBandeja("activos");
    setVista("ticket");
    cargarResumen();
  };

  const alActualizarTicket = (tk) => {
    setTicket(tk);
    cargarResumen();
  };

  const volver = () => {
    setVista("lista");
    setTicket(null);
    cargarResumen();
  };

  const desplazado = RUTAS_DESPLAZADO.some((r) => location.pathname.startsWith(r));
  const horario = resumen.horario;
  const noLeidos = resumen.noLeidos || 0;
  // Mensajes sin leer también en el favicon (arriba a la derecha), para verlo desde otra pestaña
  useContadorFavicon(noDisponible ? 0 : noLeidos);

  return (
    <div className={`cew ${desplazado ? "cew--desplazado" : ""}`}>
      <section
        className={`cew-ventana ${abierto ? "cew-ventana--abierta" : ""}`}
        aria-hidden={!abierto}
        aria-label={t("chatElite.widget.titulo")}
      >
        <header className="cew-cabecera">
          <span className="cew-cabecera__icono"><Headset size={18} /></span>
          <div className="cew-cabecera__texto">
            <span className="cew-cabecera__titulo">{t("chatElite.widget.titulo")}</span>
            <span className="cew-cabecera__subtitulo">{t("chatElite.widget.subtitulo")}</span>
          </div>
          <button type="button" className="cew-icono cew-icono--claro" onClick={() => setAbierto(false)} aria-label={t("chatElite.widget.cerrar")}>
            <X size={18} />
          </button>
        </header>

        {suplantador && (
          <div className="ce-aviso ce-aviso--suplantacion">
            <UserCog size={15} />
            <span>{t("chatElite.widget.suplantando", { aaff: user.nombre, nombre: suplantador.nombre, rol: suplantador.rol })}</span>
          </div>
        )}
        {horario && !horario.enHorario && !noDisponible && (
          <div className="ce-aviso ce-aviso--info">
            <Clock size={15} />
            <span>{t("chatElite.widget.fueraHorario", { horario: textoHorario(horario, t) })}</span>
          </div>
        )}
        {!conectado && !noDisponible && abierto && (
          <div className="ce-aviso ce-aviso--info"><WifiOff size={15} /><span>{t("chatElite.comun.reconectando")}</span></div>
        )}

        <div className="cew-cuerpo">
          {noDisponible ? (
            <div className="cew-vacio">
              <Lock size={32} strokeWidth={1.4} />
              <strong>{t("chatElite.widget.noDisponible")}</strong>
              <span>{t("chatElite.widget.noDisponibleTexto")}</span>
            </div>
          ) : vista === "ticket" && ticket ? (
            <ConversacionAaff
              key={ticket.id}
              ticket={ticket}
              socket={socket}
              miUserId={user.user_id}
              noLeidosAlAbrir={noLeidosAlAbrir}
              onVolver={volver}
              onTicketActualizado={alActualizarTicket}
              onNueva={() => setVista("nueva")}
              visible={abierto}
            />
          ) : vista === "nueva" ? (
            <div className="cew-conversacion">
              <div className="cew-cabecera-ticket">
                <button type="button" className="cew-icono" onClick={() => setVista("lista")} aria-label={t("chatElite.widget.volver")}>
                  <ArrowLeft size={18} />
                </button>
                <div className="cew-cabecera-ticket__info">
                  <span className="cew-cabecera-ticket__asunto">{t("chatElite.widget.nuevaConsulta")}</span>
                </div>
              </div>
              <NuevaConsulta key={claveNueva} onCreado={alCrear} />
            </div>
          ) : (
            <ListaConsultas
              bandeja={bandeja}
              onCambiarBandeja={setBandeja}
              tickets={tickets[bandeja] || []}
              cargando={cargandoLista}
              onAbrir={abrirTicket}
              onNueva={() => setVista("nueva")}
              contadorActivos={resumen.activos || 0}
            />
          )}
        </div>
      </section>

      <button
        type="button"
        className={`cew-boton ${abierto ? "cew-boton--activo" : ""}`}
        onClick={() => setAbierto((v) => !v)}
        aria-label={abierto ? t("chatElite.widget.cerrar") : t("chatElite.widget.abrir")}
        aria-expanded={abierto}
      >
        <span className="cew-boton__icono cew-boton__icono--abrir"><Headset size={24} /></span>
        <span className="cew-boton__icono cew-boton__icono--cerrar"><X size={24} /></span>
        {noLeidos > 0 && !abierto && (
          <span className="cew-boton__badge" aria-label={t("chatElite.widget.noLeidos", { count: noLeidos })}>
            {noLeidos > 9 ? "9+" : noLeidos}
          </span>
        )}
      </button>
    </div>
  );
};

/**
 * Chat Elite del AAFF (plan LegalIA Elite). Tambien lo ve quien entra como el AAFF.
 */
const ChatEliteWidget = () => {
  const user = useContext(UsuarioContext);
  if (!puedeUsarWidgetChatElite(user)) return null;
  return <Widget user={user} />;
};

export default ChatEliteWidget;
